import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const SECTIONS = ["38 X 38", "40 X 40", "40 X 60", "40 X 80"];

export default function SlottedSettings() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/slotted/config").then((r) => setCfg(r.data));
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/slotted/config", cfg);
      setCfg(data);
      toast.success("Slotted settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!cfg)
    return (
      <Layout>
        <div className="p-12 label-tech">Loading…</div>
      </Layout>
    );

  const setStrip = (section, v) =>
    setCfg({ ...cfg, section_strip_m: { ...cfg.section_strip_m, [section]: Number(v) } });

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin · Slotted Angle"
        title="Slotted Angle Settings"
        subtitle="Strip widths per section, default picks, pricing. Dealers/employees select section & thicknesses manually on each quote."
        actions={
          <button onClick={save} className="btn-primary" data-testid="sa-settings-save">
            <Save size={16} /> Save
          </button>
        }
      />

      <div className="p-8 space-y-6">
        <div className="panel p-6" data-testid="sa-strips">
          <div className="label-tech mb-3">Strip widths per section (m)</div>
          <div className="text-xs text-ink-muted mb-4">
            Flat steel strip width used to form each slotted-angle section. Drives weight calculation.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SECTIONS.map((s) => (
              <div key={s}>
                <label className="label-tech text-[0.65rem] mb-1 block">{s}</label>
                <input
                  type="number"
                  step="0.001"
                  value={cfg.section_strip_m?.[s] ?? 0}
                  onChange={(e) => setStrip(s, e.target.value)}
                  className="input-tech mono text-xs"
                  data-testid={`sa-strip-${s.replace(/\s/g, "")}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="panel p-6" data-testid="sa-defaults">
          <div className="label-tech mb-3">Default selections</div>
          <div className="text-xs text-ink-muted mb-4">Pre-filled values on a new quote. Users can change per quote.</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Default section</label>
              <select
                value={cfg.default_section}
                onChange={(e) => setCfg({ ...cfg, default_section: e.target.value })}
                className="input-tech mono text-xs"
              >
                {SECTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Default angle thickness (mm)</label>
              <input
                type="number"
                step="0.1"
                value={cfg.default_angle_thickness_mm}
                onChange={(e) => setCfg({ ...cfg, default_angle_thickness_mm: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Default shelf thickness (mm)</label>
              <input
                type="number"
                step="0.05"
                value={cfg.default_shelf_thickness_mm}
                onChange={(e) => setCfg({ ...cfg, default_shelf_thickness_mm: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Default partition thickness (mm)</label>
              <input
                type="number"
                step="0.05"
                value={cfg.default_partition_thickness_mm}
                onChange={(e) => setCfg({ ...cfg, default_partition_thickness_mm: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Default cover thickness (mm)</label>
              <input
                type="number"
                step="0.05"
                value={cfg.default_cover_thickness_mm}
                onChange={(e) => setCfg({ ...cfg, default_cover_thickness_mm: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
          </div>
        </div>

        <div className="panel p-6" data-testid="sa-fixed-weights">
          <div className="label-tech mb-3">Fixed weights & factors</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Nuts & bolts / joint (kg)</label>
              <input
                type="number"
                step="0.001"
                value={cfg.nuts_bolts_per_joint_kg}
                onChange={(e) => setCfg({ ...cfg, nuts_bolts_per_joint_kg: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Fitting allowance (kg)</label>
              <input
                type="number"
                step="0.1"
                value={cfg.fitting_kg || 0}
                onChange={(e) => setCfg({ ...cfg, fitting_kg: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Density factor</label>
              <input
                type="number"
                step="0.01"
                value={cfg.density_factor}
                onChange={(e) => setCfg({ ...cfg, density_factor: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Cover scrap factor (0.03 = +3%)</label>
              <input
                type="number"
                step="0.01"
                value={cfg.scrap_factor}
                onChange={(e) => setCfg({ ...cfg, scrap_factor: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Angle scrap factor (0.87 = 13%)</label>
              <input
                type="number"
                step="0.01"
                min="0.5"
                max="1"
                value={cfg.angle_scrap_factor ?? 0.87}
                onChange={(e) => setCfg({ ...cfg, angle_scrap_factor: Number(e.target.value) })}
                className="input-tech mono text-xs"
                data-testid="sa-angle-scrap"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Shelf scrap factor (0.87 = 13%)</label>
              <input
                type="number"
                step="0.01"
                min="0.5"
                max="1"
                value={cfg.shelf_scrap_factor ?? 0.87}
                onChange={(e) => setCfg({ ...cfg, shelf_scrap_factor: Number(e.target.value) })}
                className="input-tech mono text-xs"
                data-testid="sa-shelf-scrap"
              />
            </div>
          </div>
          <div className="text-[0.7rem] text-ink-muted mt-3">
            <b>Angle / Shelf scrap factors</b> are applied as a multiplier on the piece weight
            (e.g. 0.87 reduces the weight by 13% to deduct expected slot-hole / cut waste).
            Set to <b>1.00</b> to disable.
          </div>
        </div>

        <div className="panel p-6" data-testid="sa-pricing">
          <div className="label-tech mb-3">Pricing</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">Base price per kg (₹)</label>
              <input
                type="number"
                step="0.5"
                value={cfg.price_per_kg}
                onChange={(e) => setCfg({ ...cfg, price_per_kg: Number(e.target.value) })}
                className="input-tech mono text-xs"
                data-testid="sa-price-per-kg"
              />
            </div>
            <div>
              <label className="label-tech text-[0.65rem] mb-1 block">GST %</label>
              <input
                type="number"
                step="0.5"
                value={cfg.gst_percent}
                onChange={(e) => setCfg({ ...cfg, gst_percent: Number(e.target.value) })}
                className="input-tech mono text-xs"
              />
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-line" data-testid="sa-thickness-prices">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="label-tech">Per-thickness price overrides</div>
                <div className="text-[0.7rem] text-ink-muted mt-1">
                  Optional. Each component uses its own thickness rate; unlisted thicknesses fall back to the base price.
                </div>
              </div>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() =>
                  setCfg({
                    ...cfg,
                    thickness_prices: { ...(cfg.thickness_prices || {}), "": 0 },
                  })
                }
                data-testid="sa-price-row-add"
              >
                + Add row
              </button>
            </div>
            <table className="w-full grid-lines text-xs">
              <thead>
                <tr>
                  <th>Thickness (mm)</th>
                  <th>Price / kg (₹)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.entries(cfg.thickness_prices || {}).map(([k, v], i) => (
                  <tr key={i}>
                    <td>
                      <input
                        type="number"
                        step="0.05"
                        value={k}
                        onChange={(e) => {
                          const next = { ...(cfg.thickness_prices || {}) };
                          const nk = e.target.value;
                          delete next[k];
                          next[nk] = Number(v);
                          setCfg({ ...cfg, thickness_prices: next });
                        }}
                        className="input-tech mono text-xs"
                        data-testid={`sa-tp-thick-${i}`}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.5"
                        value={v}
                        onChange={(e) => {
                          const next = { ...(cfg.thickness_prices || {}) };
                          next[k] = Number(e.target.value);
                          setCfg({ ...cfg, thickness_prices: next });
                        }}
                        className="input-tech mono text-xs"
                        data-testid={`sa-tp-price-${i}`}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-danger text-xs py-1"
                        onClick={() => {
                          const next = { ...(cfg.thickness_prices || {}) };
                          delete next[k];
                          setCfg({ ...cfg, thickness_prices: next });
                        }}
                        data-testid={`sa-tp-del-${i}`}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
                {Object.keys(cfg.thickness_prices || {}).length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-ink-faint italic py-4">
                      No overrides — all thicknesses use the base price.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
